import { Box, Chip, Stack, Avatar, IconButton, Tooltip } from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { type UserWithPermission } from "@/api/userService";
import { formattedDateTime } from "@/utils/date";
import { usePermissions } from "@/hooks/usePermissions";

interface RoleManagementTableProps {
  users: UserWithPermission[];
  isLoading: boolean;
  handleEdit: (user: UserWithPermission | null) => () => void;
}

// เนื่องจาก DataGrid ไม่รองรับความกว้างแบบ "auto" สำหรับคอลัมน์
// จึงต้องคำนวนความกว้างเองแบบคร่าวๆ
////////////////////////////////////////////////////////////////////////////////////
// ค่าคร่าวๆของ MUI
const FONT_SIZE_PX = 14; // 14px
const CHAR_PX = 0.55 * FONT_SIZE_PX; // ≈ 6.6px ต่อ 1 ตัวอักษร
const CHIP_LABEL_PAD_X = 6; // padding ซ้าย/ขวาภายใน label
const CHIP_SIDE_PAD_X = 6; // เผื่อขอบ/ตัวชิป
const CHIP_MIN_WIDTH = 32; // กันไม่ให้เล็กเกิน
const CHIP_GAP = 8; // spacing ระหว่างชิปใน <Stack spacing={1}>
const CELL_LR_PADDING = 6; // padding ซ้าย/ขวาของ cell

// คำนวนความกว้างชิป
const estimateChipWidth = (label: string) => {
  const textW = label.length * CHAR_PX;
  const chipW = Math.ceil(textW) + 2 * CHIP_LABEL_PAD_X + 2 * CHIP_SIDE_PAD_X;
  return Math.max(CHIP_MIN_WIDTH, chipW);
};

// คำนวนความกว้างคอลัมน์
const estimateChipsRowWidth = (labels: string[]) => {
  if (!labels || labels.length === 0) return 100;
  const sumChips = labels.map(estimateChipWidth).reduce((a, b) => a + b, 0);
  const gaps = CHIP_GAP * (labels.length - 1);
  return Math.max(100, Math.min(1200, sumChips + gaps + CELL_LR_PADDING * 2));
};

// เลือกคอลัมน์ที่กว้างที่สุด
const estimateColumnWidthFromRows = (
  rows: Array<{ user_groups: string[] }>
) => {
  let maxW = 100;
  for (const r of rows) {
    maxW = Math.max(maxW, estimateChipsRowWidth(r.user_groups ?? []));
  }
  return maxW;
};
////////////////////////////////////////////////////////////////////////////////////

const RoleManagementTable = ({
  users,
  isLoading,
  handleEdit,
}: RoleManagementTableProps) => {
  const canEdit = usePermissions().canEdit("role");

  const columns: GridColDef<UserWithPermission>[] = [
    {
      field: "Action",
      headerName: "Action",
      width: 100,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <Tooltip
            title={"แก้ไข"}
            placement="top"
            slotProps={{
              popper: {
                modifiers: [{ name: "offset", options: { offset: [0, -10] } }],
              },
            }}
          >
            <IconButton onClick={handleEdit(param.row)} disabled={!canEdit}>
              <BorderColorIcon color={canEdit ? "primary" : "disabled"} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "user_image",
      headerName: "รูป",
      width: 60,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Avatar
            sx={{
              bgcolor: "white",
              color: "#009688",
              mr: 1,
              width: 35,
              height: 35,
              "& img": {
                objectPosition: "center 25%",
              },
            }}
            src={param.row.user_image ?? undefined}
          />
        </Box>
      ),
      sortable: false,
    },
    {
      field: "user_id",
      headerName: "รหัสพนักงาน",
      width: 120,
      headerClassName: "custom-header",
    },
    {
      field: "full_name",
      headerName: "ชื่อ - นามสกุล",
      minWidth: 150,
      flex: 0.2,
      headerClassName: "custom-header",
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 200,
      flex: 0.2,
      headerClassName: "custom-header",
    },
    {
      field: "department",
      headerName: "หน่วยงาน",
      minWidth: 160,
      flex: 0.2,
      valueGetter: (_value, row) => `${row.department || ""}`,
      headerClassName: "custom-header",
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Chip
            label={param.row.status}
            sx={{
              backgroundColor:
                param.row.status.toLocaleLowerCase() === "active"
                  ? "success.light"
                  : "error.light",
              color: "#fff",
              fontWeight: "600",
              fontSize: "0.8rem",
              width: "80px",
            }}
            key={param.row.status}
          />
        </Box>
      ),
    },
    {
      field: "user_groups",
      headerName: "บทบาท",
      width: estimateColumnWidthFromRows(users),
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Stack
          direction="row"
          spacing={1}
          sx={{ display: "flex", alignItems: "center", height: "100%" }}
        >
          {param.row.user_groups?.map((group) => (
            <Chip label={group} color="info" key={group} />
          ))}
        </Stack>
      ),
    },
    {
      field: "buyer_code",
      headerName: "Buyer Code",
      width: 120,
      valueGetter: (_value, row) => `${row.buyer_code || "-"}`,
      headerClassName: "custom-header",
    },
    {
      field: "updated_by",
      headerName: "Updated By",
      width: 100,
      valueGetter: (_value, row) => `${row.updated_by || "-"}`,
      headerClassName: "custom-header",
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      width: 160,
      valueGetter: (_value, row) =>
        `${formattedDateTime(row.updated_at) || "-"}`,
      headerClassName: "custom-header",
    },
  ];

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <DataGrid
        rows={users}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.user_id}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 20, 50, 100]}
        disableRowSelectionOnClick
        disableColumnSelector
        disableColumnFilter
        disableColumnMenu
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
        }}
      />
    </Box>
  );
};

export default RoleManagementTable;
